/**
 * Minimal logger for the app SDK.
 *
 * The SDK is meant to run inside an embedded app (browser or a lightweight
 * Node service), so it deliberately does not pull in the framework's
 * `LogStore` / SQLite logging stack. Authors can pass their own {@link AppLogger}
 * (e.g. a bridge to their app's telemetry) or accept the console-backed default.
 *
 * @category App SDK
 * @since 3.4.0
 */

/** Structured leveled logger the SDK and capability handlers emit through. */
export interface AppLogger {
  debug(message: string, data?: unknown): void;
  info(message: string, data?: unknown): void;
  warn(message: string, data?: unknown): void;
  error(message: string, data?: unknown): void;
}

/** A logger that discards everything. The default when none is supplied. */
export const noopLogger: AppLogger = {
  debug() {},
  info() {},
  warn() {},
  error() {},
};

/**
 * Console-backed logger, prefixing every line with `[skaile-app]`. Opt in by
 * passing `logger: consoleLogger` to {@link createSkaileApp} during development.
 */
export const consoleLogger: AppLogger = {
  debug(message, data) {
    console.debug(`[skaile-app] ${message}`, ...(data !== undefined ? [data] : []));
  },
  info(message, data) {
    console.info(`[skaile-app] ${message}`, ...(data !== undefined ? [data] : []));
  },
  warn(message, data) {
    console.warn(`[skaile-app] ${message}`, ...(data !== undefined ? [data] : []));
  },
  error(message, data) {
    console.error(`[skaile-app] ${message}`, ...(data !== undefined ? [data] : []));
  },
};
